import {
    FGGridContainer,
    FGGridItem,
    FGIcons,
    FGLink,
    FGStack,
    FGTypography,
} from '@fg/design-system';
import {
    GridHeaderColTypography,
    GridItemTypography,
    InsightAndEventBadge,
    InsightAndEventContainer,
    InsightAndEventLoadingContainer,
    InsightAndEventSeparator,
} from 'components/eventsFeed/EventDisplay';
import { MOMENTJS_DATE_DISPLAY_FORMAT } from '../../../constants';
import { IActiveCountry, IPrediction, VIEW } from 'interfaces';
import moment from 'moment';
import React from 'react';
import { mapWithKeys } from 'utils/lodashUtils';
import { CountryBadge } from 'components/eventsFeed/CountryBadge';

type Props = {
    prediction: IPrediction;
    isLoading: boolean;
    error: string;
    countriesById: { [countryID: number]: IActiveCountry };
};

const PredictionDisplay = React.memo(
    ({ prediction, isLoading, error, countriesById }: Props) => {
        if (isLoading) {
            return <InsightAndEventLoadingContainer />;
        }
        if (error) {
            return (
                <FGStack justifyContent="center" style={{ marginTop: 32 }}>
                    <FGTypography
                        variant="headline4"
                        tag="label"
                        color="strong">
                        {error}
                    </FGTypography>
                </FGStack>
            );
        }
        return (
            <InsightAndEventContainer>
                <FGStack gap={4} style={{ width: 'min-content' }}>
                    <CountryBadge
                        country={countriesById[prediction.country_id]}
                    />
                    <InsightAndEventBadge>
                        <FGTypography
                            variant="body4"
                            tag="label"
                            cssStyle={{ whiteSpace: 'nowrap' }}>
                            {prediction.tags}
                        </FGTypography>
                    </InsightAndEventBadge>
                </FGStack>
                <FGTypography
                    tag="h2"
                    variant="headline2"
                    color="strong"
                    cssStyle={{ margin: '12px 0 24px' }}>
                    {prediction?.abbreviation}
                </FGTypography>
                <FGTypography
                    tag="label"
                    variant="body2"
                    cssStyle={{ marginBottom: 12 }}>
                    {prediction?.description}
                </FGTypography>
                <FGTypography
                    tag="label"
                    variant="label3"
                    cssStyle={{ marginBottom: 8 }}>
                    Insight
                </FGTypography>
                {mapWithKeys((title, insightId) => {
                    return (
                        <FGLink
                            appearance="blue"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={`#/${VIEW.RISK_INTELLIGENCE}/${VIEW.INSIGHTS}/${insightId}`}>
                            <FGStack gap={8} alignItems="center">
                                <FGIcons size={12} icon="PopOut" />
                                <FGTypography
                                    tag="label"
                                    variant="link2"
                                    cssStyle={{ cursor: 'pointer' }}>
                                    {title}
                                </FGTypography>
                            </FGStack>
                        </FGLink>
                    );
                }, prediction.insight_refs)}
                <InsightAndEventSeparator />

                <FGGridContainer
                    isFluid
                    columnGap={0}
                    style={{ padding: 0, gap: 0 }}>
                    <FGGridItem sm={2}>
                        <GridHeaderColTypography>
                            Outcome
                        </GridHeaderColTypography>
                    </FGGridItem>
                    <FGGridItem sm={2}>
                        <GridHeaderColTypography>
                            Call made
                        </GridHeaderColTypography>
                    </FGGridItem>
                    <FGGridItem sm={2}>
                        <GridHeaderColTypography>
                            Realization
                        </GridHeaderColTypography>
                    </FGGridItem>
                    <FGGridItem sm={2}>
                        <GridHeaderColTypography>
                            Confidence
                        </GridHeaderColTypography>
                    </FGGridItem>
                    <FGGridItem sm={2}>
                        <GridHeaderColTypography>
                            Window
                        </GridHeaderColTypography>
                    </FGGridItem>
                    <FGGridItem sm={2}>
                        {prediction.consensus && (
                            <GridHeaderColTypography>
                                Consensus
                            </GridHeaderColTypography>
                        )}
                    </FGGridItem>
                    <FGGridItem sm={2}>
                        <GridItemTypography>
                            {prediction.outcome}
                        </GridItemTypography>
                    </FGGridItem>
                    <FGGridItem sm={2}>
                        <GridItemTypography>
                            {moment(Date.parse(prediction.call_date)).format(
                                MOMENTJS_DATE_DISPLAY_FORMAT
                            )}
                        </GridItemTypography>
                    </FGGridItem>
                    <FGGridItem sm={2}>
                        <GridItemTypography>
                            {moment(
                                Date.parse(prediction.realization_date)
                            ).format(MOMENTJS_DATE_DISPLAY_FORMAT)}
                        </GridItemTypography>
                    </FGGridItem>
                    <FGGridItem sm={2}>
                        <GridItemTypography>
                            {prediction.confidence}
                        </GridItemTypography>
                    </FGGridItem>
                    <FGGridItem sm={2}>
                        <GridItemTypography>
                            {prediction.window}
                        </GridItemTypography>
                    </FGGridItem>
                    <FGGridItem sm={2}>
                        <GridItemTypography>
                            {prediction.consensus}
                        </GridItemTypography>
                    </FGGridItem>
                </FGGridContainer>
            </InsightAndEventContainer>
        );
    }
);

export default PredictionDisplay;
